import React, { useState, useEffect, useRef } from 'react';
import { Bell, Trash2, Check, X, Mail, BookOpen, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { EMAIL_AUTH_ERROR_EVENT, type EmailAuthError } from '@/hooks/useEmailSync';

interface Notification {
  id: string;
  type: 'todo' | 'email' | 'knowledge' | 'email_auth';
  priority: 'overdue' | 'urgent' | 'reminder' | 'info';
  title: string;
  description: string;
  date: Date;
  customerId?: string;
  todoId?: string;
  emailId?: string;
  knowledgeArticleId?: string;
  emailAccountId?: string;
  emailAddress?: string;
}

export const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(() => {
    // Load dismissed notifications from localStorage
    const saved = localStorage.getItem('dismissedNotifications');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [isOpen, setIsOpen] = useState(false);
  const [swipeStates, setSwipeStates] = useState<Record<string, { startX: number; currentX: number; isSwiping: boolean }>>({});
  const { user } = useAuth();
  const navigate = useNavigate();

  // Save dismissed notifications to localStorage
  useEffect(() => {
    localStorage.setItem('dismissedNotifications', JSON.stringify([...dismissedNotifications]));
  }, [dismissedNotifications]);

  const fetchNotifications = async () => {
    if (!user) return;

    const now = new Date();
    const tomorrow = addDays(now, 1);
    const threeDaysFromNow = addDays(now, 3);

    const allNotifications: Notification[] = [];

    // Fetch todos
    const { data: todos } = await supabase
      .from('todos')
      .select('*, customers(first_name, last_name)')
      .eq('user_id', user.id)
      .eq('completed', false)
      .not('due_date', 'is', null);

    if (todos) {
      todos.forEach((todo) => {
        const dueDate = new Date(todo.due_date);
        const customerName = todo.customers 
          ? `${todo.customers.first_name} ${todo.customers.last_name}`.trim()
          : 'Allgemein';

        // Overdue todos
        if (isBefore(dueDate, now)) {
          allNotifications.push({
            id: `todo-overdue-${todo.id}`,
            type: 'todo',
            priority: 'overdue',
            title: 'Überfälliges Todo',
            description: `${todo.title} - ${customerName}`,
            date: dueDate,
            customerId: todo.customer_id,
            todoId: todo.id,
          });
        }
        // Urgent todos (2-3 days before due)
        else if (isBefore(dueDate, threeDaysFromNow) && isAfter(dueDate, tomorrow)) {
          allNotifications.push({
            id: `todo-urgent-${todo.id}`,
            type: 'todo',
            priority: 'urgent',
            title: 'Todo fällig in 2-3 Tagen',
            description: `${todo.title} - ${customerName}`,
            date: dueDate,
            customerId: todo.customer_id,
            todoId: todo.id,
          });
        }
        // Today's todos
        else if (isBefore(dueDate, tomorrow) && isAfter(dueDate, now)) {
          allNotifications.push({
            id: `todo-today-${todo.id}`,
            type: 'todo',
            priority: 'urgent',
            title: 'Todo heute fällig',
            description: `${todo.title} - ${customerName}`,
            date: dueDate,
            customerId: todo.customer_id,
            todoId: todo.id,
          });
        }
      });
    }

    // Fetch unread emails (received in last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const { data: unreadEmails } = await supabase
      .from('emails')
      .select('id, subject, from_address, received_at')
      .eq('user_id', user.id)
      .eq('is_read', false)
      .eq('status', 'received')
      .gte('received_at', sevenDaysAgo.toISOString())
      .order('received_at', { ascending: false })
      .limit(10);

    if (unreadEmails) {
      unreadEmails.forEach((email) => {
        const receivedDate = new Date(email.received_at || new Date());
        allNotifications.push({
          id: `email-${email.id}`,
          type: 'email',
          priority: 'info',
          title: 'Neue E-Mail',
          description: email.subject || `Von: ${email.from_address}`,
          date: receivedDate,
          emailId: email.id,
        });
      });
    }

    // Fetch user's team_id for knowledge base articles
    const { data: profile } = await supabase
      .from('profiles')
      .select('team_id')
      .eq('user_id', user.id)
      .single();

    if (profile?.team_id) {
      // Get last viewed timestamp from localStorage
      const lastViewedKB = localStorage.getItem(`kb_last_viewed_${user.id}`);
      const lastViewedDate = lastViewedKB ? new Date(lastViewedKB) : new Date(0);

      // Fetch new knowledge base articles
      const { data: kbArticles } = await supabase
        .from('team_knowledge_base')
        .select('id, title, created_at, created_by')
        .eq('team_id', profile.team_id)
        .neq('created_by', user.id) // Don't notify about own articles
        .gt('created_at', lastViewedDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (kbArticles) {
        kbArticles.forEach((article) => {
          const createdDate = new Date(article.created_at);
          allNotifications.push({
            id: `kb-${article.id}`,
            type: 'knowledge',
            priority: 'info',
            title: 'Neuer Wissensbank-Artikel',
            description: article.title,
            date: createdDate,
            knowledgeArticleId: article.id,
          });
        });
      }
    }

    // Sort by date (newest first)
    allNotifications.sort((a, b) => b.date.getTime() - a.date.getTime());

    setNotifications(allNotifications);
  };

  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Listen for email auth errors
  useEffect(() => {
    const handleEmailAuthError = (event: CustomEvent<EmailAuthError>) => {
      const { accountId, emailAddress } = event.detail;
      
      // Add email auth error notification
      const authNotification: Notification = {
        id: `email-auth-${accountId}`,
        type: 'email_auth',
        priority: 'urgent',
        title: 'E-Mail-Anmeldung erforderlich',
        description: `Passwort für ${emailAddress} ungültig`,
        date: new Date(),
        emailAccountId: accountId,
        emailAddress: emailAddress,
      };
      
      setNotifications(prev => {
        // Remove existing auth notification for this account
        const filtered = prev.filter(n => n.id !== authNotification.id);
        return [authNotification, ...filtered];
      });
      
      // Remove from dismissed so it shows again
      setDismissedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(authNotification.id);
        return newSet;
      });
    };

    window.addEventListener(EMAIL_AUTH_ERROR_EVENT, handleEmailAuthError as EventListener);
    return () => {
      window.removeEventListener(EMAIL_AUTH_ERROR_EVENT, handleEmailAuthError as EventListener);
    };
  }, []);

  // Realtime subscription for todos
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notification-todos')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'todos',
        filter: `user_id=eq.${user.id}`
      }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Realtime subscription for emails
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notification-emails')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'emails',
        filter: `user_id=eq.${user.id}`
      }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleNotificationClick = (notification: Notification) => {
    // Only dismiss non-todo and non-email-auth notifications on click
    // Todos should only disappear when completed (via the check button)
    // Email auth should disappear when re-authenticated
    if (notification.type !== 'todo' && notification.type !== 'email_auth') {
      setDismissedNotifications(prev => new Set([...prev, notification.id]));
    }
    
    // Handle different notification types
    if (notification.type === 'email_auth' && notification.emailAccountId) {
      // Navigate to email with re-auth state
      navigate('/email', { 
        state: { 
          showReAuth: true,
          reAuthAccountId: notification.emailAccountId,
          reAuthEmailAddress: notification.emailAddress
        } 
      });
    } else if (notification.type === 'email' && notification.emailId) {
      navigate('/email', { state: { selectedEmailId: notification.emailId } });
    } else if (notification.type === 'knowledge' && notification.knowledgeArticleId) {
      // Update last viewed timestamp when opening KB
      localStorage.setItem(`kb_last_viewed_${user?.id}`, new Date().toISOString());
      navigate('/archive', { state: { selectedArticleId: notification.knowledgeArticleId } });
    } else if (notification.type === 'todo') {
      // Navigate to todos page with the todo to open
      navigate('/todos', { 
        state: { 
          selectedTodoId: notification.todoId,
          selectedCustomerId: notification.customerId,
          openTodo: true
        } 
      });
    } else if (notification.customerId) {
      navigate('/customers', { state: { selectedCustomerId: notification.customerId } });
    } else {
      navigate('/todos');
    }
    setIsOpen(false);
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "destructive" => {
    switch (priority) {
      case 'overdue': return 'destructive';
      case 'urgent': return 'default';
      case 'reminder': return 'secondary';
      case 'info': return 'secondary';
      default: return 'secondary';
    }
  };

  const handleTouchStart = (e: React.TouchEvent, notificationId: string) => {
    const touch = e.touches[0];
    setSwipeStates(prev => ({
      ...prev,
      [notificationId]: {
        startX: touch.clientX,
        currentX: touch.clientX,
        isSwiping: true
      }
    }));
  };

  const handleTouchMove = (e: React.TouchEvent, notificationId: string) => {
    if (!swipeStates[notificationId]?.isSwiping) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStates[notificationId].startX;
    
    // Only allow left swipe (negative deltaX)
    if (deltaX < 0) {
      setSwipeStates(prev => ({
        ...prev,
        [notificationId]: {
          ...prev[notificationId],
          currentX: touch.clientX
        }
      }));
    }
  };

  const handleTouchEnd = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState) return;

    const deltaX = swipeState.currentX - swipeState.startX;
    const deleteThreshold = -120;
    const markReadThreshold = -60;

    if (deltaX < deleteThreshold) {
      handleDismissNotification(notificationId);
    } else if (deltaX < markReadThreshold) {
      handleMarkAsRead(notificationId);
    }

    setSwipeStates(prev => {
      const newState = { ...prev };
      delete newState[notificationId];
      return newState;
    });
  };

  const handleDismissNotification = (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    setDismissedNotifications(prev => new Set([...prev, notificationId]));
    
    toast.success("Benachrichtigung entfernt", {
      description: notification.title,
      action: {
        label: "Rückgängig",
        onClick: () => handleUndoDismiss(notificationId)
      },
    });
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    // Mark as dismissed (read)
    setDismissedNotifications(prev => new Set([...prev, notificationId]));
    
    toast.info("Als gelesen markiert", {
      description: notification.title,
      action: {
        label: "Rückgängig",
        onClick: () => handleUndoDismiss(notificationId)
      },
    });
  };

  const handleCompleteTodo = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!notification.todoId) return;

    try {
      const { error } = await supabase
        .from('todos')
        .update({ completed: true })
        .eq('id', notification.todoId);

      if (error) throw error;

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      
      toast.success("Todo erledigt!", {
        description: notification.description
      });
    } catch (error) {
      console.error('Error completing todo:', error);
      toast.error("Fehler beim Abschließen des Todos");
    }
  };

  const handleUndoDismiss = (notificationId: string) => {
    setDismissedNotifications(prev => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  const handleClearAll = () => {
    const currentIds = notifications.map(n => n.id);
    setDismissedNotifications(prev => new Set([...prev, ...currentIds]));
    toast.success("Alle Benachrichtigungen entfernt");
  };

  const getSwipeTransform = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState?.isSwiping) return 'translateX(0px)';
    
    const deltaX = swipeState.currentX - swipeState.startX;
    return `translateX(${Math.min(deltaX, 0)}px)`;
  };

  const getSwipeProgress = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState?.isSwiping) return 0;
    
    const deltaX = swipeState.currentX - swipeState.startX;
    return Math.abs(deltaX);
  };

  const visibleNotifications = notifications.filter(n => !dismissedNotifications.has(n.id));
  const notificationCount = visibleNotifications.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 shrink-0">
          <Bell className="h-5 w-5" />
          {notificationCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold px-1 leading-none">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Benachrichtigungen</h3>
          </div>
          {notificationCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Alle löschen
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {visibleNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Keine offenen Todos</p>
            </div>
          ) : (
            <div className="divide-y">
              {visibleNotifications.map((notification) => {
                const swipeProgress = getSwipeProgress(notification.id);
                const isDeleting = swipeProgress > 120;
                const isMarking = swipeProgress > 60 && swipeProgress <= 120;
                
                return (
                  <div
                    key={notification.id}
                    className="relative overflow-hidden"
                  >
                    {/* Action background */}
                    <div className={cn(
                      "absolute inset-0 flex items-center justify-end px-4 transition-colors",
                      isDeleting ? "bg-destructive" : isMarking ? "bg-primary" : "bg-muted"
                    )}>
                      {isDeleting ? (
                        <div className="flex items-center gap-2 text-destructive-foreground">
                          <Trash2 className="h-4 w-4" />
                          <span className="text-sm font-medium">Löschen</span>
                        </div>
                      ) : isMarking ? (
                        <div className="flex items-center gap-2 text-primary-foreground">
                          <Check className="h-4 w-4" />
                          <span className="text-sm font-medium">Gelesen</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="text-xs">← Wischen</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div
                      className="p-4 bg-popover cursor-pointer transition-transform select-none relative"
                      style={{
                        transform: getSwipeTransform(notification.id),
                      }}
                      onClick={() => handleNotificationClick(notification)}
                      onTouchStart={(e) => handleTouchStart(e, notification.id)}
                      onTouchMove={(e) => handleTouchMove(e, notification.id)}
                      onTouchEnd={() => handleTouchEnd(notification.id)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Type icon */}
                        <div className={cn(
                          "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                          notification.type === 'email_auth' ? "bg-amber-100 text-amber-600" :
                          notification.type === 'email' ? "bg-blue-100 text-blue-600" :
                          notification.type === 'knowledge' ? "bg-purple-100 text-purple-600" :
                          notification.priority === 'overdue' ? "bg-red-100 text-red-600" :
                          "bg-orange-100 text-orange-600"
                        )}>
                          {notification.type === 'email_auth' ? (
                            <KeyRound className="h-4 w-4" />
                          ) : notification.type === 'email' ? (
                            <Mail className="h-4 w-4" />
                          ) : notification.type === 'knowledge' ? (
                            <BookOpen className="h-4 w-4" />
                          ) : (
                            <Bell className="h-4 w-4" />
                          )}
                        </div>
                        
                        <div className="flex-1 space-y-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">
                              {notification.title}
                            </span>
                            <Badge 
                              variant={getPriorityBadgeVariant(notification.priority)}
                              className="text-xs shrink-0"
                            >
                              {notification.priority === 'overdue' ? 'Überfällig' : 
                               notification.priority === 'urgent' ? 'Dringend' : 
                               notification.priority === 'info' ? 'Neu' : 'Erinnerung'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {notification.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {notification.type === 'todo' 
                              ? `Fällig: ${format(notification.date, 'dd.MM.yyyy HH:mm')}`
                              : format(notification.date, 'dd.MM.yyyy HH:mm')
                            }
                          </p>
                        </div>
                        
                        {/* Quick complete button - only for todos */}
                        {notification.type === 'todo' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                            onClick={(e) => handleCompleteTodo(notification, e)}
                            title="Todo erledigen"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer hint */}
        {visibleNotifications.length > 0 && (
          <div className="p-2 border-t bg-muted/30">
            <p className="text-xs text-center text-muted-foreground">
              Nach links wischen zum Entfernen
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
